import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, Shield, Bell, CreditCard, Sparkles, Gift, AlertTriangle, Check, Loader2, Phone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Preference categories configuration
const PREFERENCE_CATEGORIES = {
  required: {
    title: 'Required Communications',
    description: 'These communications are required to service your policy and cannot be disabled.',
    items: [
      {
        id: 'policy_updates',
        label: 'Policy and Coverage Updates',
        description: 'Critical information about your policy terms, renewals, and coverage changes',
        icon: Shield,
        required: true
      },
      {
        id: 'claims_notifications',
        label: 'Claims and Account Notifications',
        description: 'Updates about your claims status, account activity, and important alerts',
        icon: Bell,
        required: true
      },
      {
        id: 'billing_reminders',
        label: 'Billing and Payment Reminders',
        description: 'Payment confirmations, billing statements, and premium due notices',
        icon: CreditCard,
        required: true
      }
    ]
  },
  optional: {
    title: 'Optional Communications',
    description: 'You can customize these preferences based on your interests.',
    items: [
      {
        id: 'product_updates',
        label: 'Product Updates and New Features',
        description: 'Learn about new coverage options, features, and service improvements',
        icon: Sparkles,
        required: false
      },
      {
        id: 'vintrakid_alerts',
        label: 'VINTrakID Insights and Alerts',
        description: 'Vehicle value tracking, market insights, and personalized recommendations',
        icon: Bell,
        required: false
      },
      {
        id: 'promotional_content',
        label: 'Promotional Offers and Partner Content',
        description: 'Exclusive deals, partner offers, and special promotions',
        icon: Gift,
        required: false
      }
    ]
  }
};

// Unsubscribe reasons
const UNSUBSCRIBE_REASONS = [
  { value: '', label: 'Select a reason (optional)' },
  { value: 'too_many_emails', label: 'I receive too many emails' },
  { value: 'not_relevant', label: 'The content is not relevant to me' },
  { value: 'already_have_coverage', label: 'I already have coverage elsewhere' },
  { value: 'no_longer_interested', label: 'I\'m no longer interested' },
  { value: 'other', label: 'Other reason' }
];

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState({
    policy_updates: true,
    claims_notifications: true,
    billing_reminders: true,
    product_updates: true,
    vintrakid_alerts: true,
    promotional_content: true
  });
  const [unsubscribeReason, setUnsubscribeReason] = useState('');
  const [changesSummary, setChangesSummary] = useState([]);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No unsubscribe token provided. Please use the link from your email.');
        setLoading(false);
        return;
      }

      try {
        // Use direct fetch to avoid axios baseURL issues
        const response = await fetch(`${API_URL}/api/email-preferences/validate?token=${encodeURIComponent(token)}`);
        const data = await response.json();

        if (data.valid) {
          setEmail(data.email);
          if (data.preferences) {
            setPreferences(data.preferences);
          }
        } else {
          setError(data.error || 'Invalid or expired link.');
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Unable to validate your request. Please try again or contact support.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Handle preference toggle
  const handlePreferenceChange = (id, checked) => {
    // Required preferences cannot be changed
    const item = [...PREFERENCE_CATEGORIES.required.items, ...PREFERENCE_CATEGORIES.optional.items]
      .find(p => p.id === id);
    
    if (item?.required) return;

    setPreferences(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  // Save preferences
  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/email-preferences/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          preferences,
          unsubscribe_reason: unsubscribeReason || null
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setChangesSummary(data.changes_summary || []);
        toast.success('Preferences updated successfully');
      } else {
        toast.error(data.detail || 'Failed to update preferences');
      }
    } catch (err) {
      console.error('Save preferences error:', err);
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  // Unsubscribe from all marketing
  const handleUnsubscribeAll = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/email-preferences/unsubscribe-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          unsubscribe_reason: unsubscribeReason || null
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setChangesSummary(data.changes_summary || []);
        setPreferences(data.updated_preferences);
        toast.success('Successfully unsubscribed from marketing emails');
      } else {
        toast.error(data.detail || 'Failed to unsubscribe');
      }
    } catch (err) {
      console.error('Unsubscribe all error:', err);
      toast.error('Failed to unsubscribe');
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Validating your request...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-8 sm:py-16">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-8">
              <img 
                src="/losspay-header-logo.png" 
                alt="LossPay" 
                className="h-8 sm:h-10"
              />
            </Link>
          </div>

          {/* Error Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                Unable to Process Request
              </h1>
              <p className="text-slate-600 mb-6">
                {error}
              </p>
              <div className="space-y-3">
                <p className="text-sm text-slate-500">
                  Need help? Contact our support team:
                </p>
                <div className="flex items-center justify-center gap-6 text-sm">
                  <a 
                    href="mailto:support@losspay.com" 
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Mail className="h-4 w-4" />
                    support@losspay.com
                  </a>
                  <a 
                    href="tel:1-833-567-7729" 
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Phone className="h-4 w-4" />
                    1-833-LOSSPAY
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-8 sm:py-16">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-8">
              <img 
                src="/losspay-header-logo.png" 
                alt="LossPay" 
                className="h-8 sm:h-10"
              />
            </Link>
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                Preferences Updated Successfully
              </h1>
              <p className="text-slate-600 mb-6">
                Your communication preferences have been saved.
              </p>

              {/* Changes Summary */}
              {changesSummary.length > 0 && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Changes Made:</h3>
                  <ul className="space-y-1">
                    {changesSummary.map((change, index) => (
                      <li key={index} className="text-sm text-slate-600 flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 text-left">
                    <strong>Important:</strong> You will continue to receive required communications 
                    including policy updates, claims notifications, and billing reminders as required 
                    to service your policy.
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3">
                <p className="text-sm text-slate-500">
                  Questions? We're here to help:
                </p>
                <div className="flex items-center justify-center gap-6 text-sm">
                  <a 
                    href="mailto:support@losspay.com" 
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Mail className="h-4 w-4" />
                    support@losspay.com
                  </a>
                  <a 
                    href="tel:1-833-567-7729" 
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Phone className="h-4 w-4" />
                    1-833-LOSSPAY
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <ComplianceFooter />
        </div>
      </div>
    );
  }

  // Main preferences form
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img 
              src="/losspay-header-logo.png" 
              alt="LossPay" 
              className="h-8 sm:h-10"
            />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-2">
            Manage Your Communication Preferences
          </h1>
          <p className="text-slate-600 max-w-md mx-auto">
            You are in control of how LossPay communicates with you. 
            Update your preferences or unsubscribe at any time.
          </p>
          {email && (
            <p className="text-sm text-slate-500 mt-2">
              Managing preferences for: <span className="font-medium">{email}</span>
            </p>
          )}
        </div>

        {/* Preferences Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Required Communications Section */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                {PREFERENCE_CATEGORIES.required.title}
              </h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {PREFERENCE_CATEGORIES.required.description}
            </p>
            
            <div className="space-y-3">
              {PREFERENCE_CATEGORIES.required.items.map((item) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.id}
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <Checkbox
                        id={item.id}
                        checked={true}
                        disabled={true}
                        className="cursor-not-allowed"
                      />
                    </div>
                    <div className="flex-grow">
                      <Label 
                        htmlFor={item.id}
                        className="text-sm font-medium text-slate-900 cursor-not-allowed flex items-center gap-2"
                      >
                        <Icon className="h-4 w-4 text-slate-400" />
                        {item.label}
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Required
                        </span>
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Optional Communications Section */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                {PREFERENCE_CATEGORIES.optional.title}
              </h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {PREFERENCE_CATEGORIES.optional.description}
            </p>
            
            <div className="space-y-3">
              {PREFERENCE_CATEGORIES.optional.items.map((item) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.id}
                    className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex-shrink-0 mt-1">
                      <Checkbox
                        id={item.id}
                        checked={preferences[item.id]}
                        onCheckedChange={(checked) => handlePreferenceChange(item.id, checked)}
                      />
                    </div>
                    <div className="flex-grow">
                      <Label 
                        htmlFor={item.id}
                        className="text-sm font-medium text-slate-900 cursor-pointer flex items-center gap-2"
                      >
                        <Icon className="h-4 w-4 text-slate-400" />
                        {item.label}
                      </Label>
                      <p className="text-xs text-slate-500 mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Unsubscribe Reason (Optional) */}
          <div className="p-6 border-b border-slate-200 bg-slate-50">
            <h3 className="text-sm font-medium text-slate-700 mb-2">
              Why are you updating your preferences? (Optional)
            </h3>
            <div className="relative">
              <select
                value={unsubscribeReason}
                onChange={(e) => setUnsubscribeReason(e.target.value)}
                className="w-full appearance-none bg-white border border-slate-300 rounded-lg px-4 py-2.5 pr-10 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {UNSUBSCRIBE_REASONS.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-6 bg-slate-50 space-y-4">
            <Button
              onClick={handleSavePreferences}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-medium"
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-50 px-2 text-slate-500">or</span>
              </div>
            </div>

            <button
              onClick={handleUnsubscribeAll}
              disabled={saving}
              className="w-full text-center text-sm text-slate-600 hover:text-red-600 transition-colors py-2"
              data-testid="unsubscribe-all-btn"
            >
              Unsubscribe from all non-essential emails
            </button>
          </div>
        </div>

        {/* Compliance Notice */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Important Notice:</strong> Certain communications are required to service your 
              policy and cannot be disabled, including policy notices, claims updates, and billing 
              communications. This ensures you receive critical information about your coverage.
            </p>
          </div>
        </div>

        {/* Footer */}
        <ComplianceFooter />
      </div>
    </div>
  );
}

// Compliance Footer Component
function ComplianceFooter() {
  return (
    <footer className="mt-6 sm:mt-12 pt-6 sm:pt-8 border-t border-slate-200">
      <div className="text-center space-y-4">
        <div className="text-sm text-slate-600">
          <p className="font-medium">LossPay Risk Technologies, Inc.</p>
          <p>Licensed Insurance Agency</p>
          <p>16192 Coastal Highway, Lewes, Delaware 19958</p>
        </div>
        
        <div className="flex items-center justify-center gap-4 text-sm">
          <a 
            href="tel:1-833-567-7729" 
            className="text-blue-600 hover:text-blue-700"
          >
            1-833-LOSSPAY
          </a>
          <span className="text-slate-300">|</span>
          <a 
            href="mailto:support@losspay.com" 
            className="text-blue-600 hover:text-blue-700"
          >
            support@losspay.com
          </a>
        </div>

        <div className="text-xs text-slate-500 max-w-lg mx-auto space-y-2">
          <p>
            Insurance products are subject to terms, conditions, and availability by state.
          </p>
          <p>
            Certain communications are required for policy servicing and cannot be opted out of.
            For full details, refer to your policy documents.
          </p>
        </div>

        <p className="text-xs text-slate-400">
          © {new Date().getFullYear()} LossPay Risk Technologies, Inc. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
